import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

const EMPTY_LIST = {
  entries: [],
  pagination: { total: 0, current_page: 1, last_page: 1, per_page: 25 },
};

const EMPTY_FILTERS = {
  institutions: [],
  actors: [],
  event_types: [],
  modules: [],
  portals: [],
  actor_types: [],
};

const EMPTY_SUMMARY = { total: 0, by_event_type: [], by_day: [], top_actors: [] };

function buildAuditQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") return;
    query.set(key, String(value));
  });
  return query;
}

function normalizeAuditList(payload) {
  const entries = Array.isArray(payload?.entries) ? payload.entries : [];
  return {
    entries,
    pagination: payload?.pagination || EMPTY_LIST.pagination,
  };
}

/**
 * GET /api/ops/activity-logs
 */
export async function fetchOpsActivityLogs(token, params = {}) {
  if (USE_MOCK_AUTH || !token) return EMPTY_LIST;

  const query = buildAuditQuery({
    page: params.page || 1,
    limit: params.limit || 25,
    provider_id: params.provider_id ?? params.provider_slug,
    provider_slug: params.provider_slug ?? params.provider_id,
    actor_slug: params.actor_slug,
    actor_type: params.actor_type,
    event_type: params.event_type,
    module: params.module,
    portal: params.portal,
    search: params.search,
    date_from: params.date_from,
    date_to: params.date_to,
  });

  const json = await apiRequest(`${OPS_AUTH.activityLogs}?${query}`, {
    method: "GET",
    token,
  });
  return normalizeAuditList(unwrapApiEnvelope(json));
}

export async function fetchOpsAuditFilters(token) {
  if (USE_MOCK_AUTH || !token) return EMPTY_FILTERS;
  const json = await apiRequest(OPS_AUTH.activityLogsFilters, {
    method: "GET",
    token,
  });
  return unwrapApiEnvelope(json) || EMPTY_FILTERS;
}

export async function fetchOpsActivityLogsSummary(token, { provider_id, provider_slug } = {}) {
  if (USE_MOCK_AUTH || !token) return EMPTY_SUMMARY;

  const query = new URLSearchParams();
  const providerKey = provider_id ?? provider_slug;
  if (providerKey) {
    query.set("provider_id", providerKey);
  }
  const suffix = query.toString() ? `?${query}` : "";
  const json = await apiRequest(`${OPS_AUTH.activityLogsSummary}${suffix}`, {
    method: "GET",
    token,
  });
  return unwrapApiEnvelope(json) || EMPTY_SUMMARY;
}

/**
 * GET /api/ops/activity-logs/sla
 */
export async function fetchOpsSlaPerformance(token, params = {}) {
  if (USE_MOCK_AUTH || !token) {
    return {
      summary: {
        total_cases: 0,
        assigned: 0,
        contacted: 0,
        payment_initiated: 0,
        payment_completed: 0,
        avg_upload_to_assign_minutes: null,
        avg_assign_to_contact_minutes: null,
        avg_upload_to_payment_minutes: null,
        avg_upload_to_payment_completed_minutes: null,
      },
      by_agent: [],
      by_group: [],
      cases: [],
    };
  }

  const query = new URLSearchParams();
  const providerKey = params.provider_id ?? params.provider_slug;
  if (providerKey) query.set("provider_id", providerKey);
  if (params.portfolio_group_slug) query.set("portfolio_group_slug", params.portfolio_group_slug);
  if (params.agent_slug) query.set("agent_slug", params.agent_slug);
  const suffix = query.toString() ? `?${query}` : "";
  const json = await apiRequest(`${OPS_AUTH.activityLogsSla}${suffix}`, {
    method: "GET",
    token,
  });
  return unwrapApiEnvelope(json) || { summary: {}, by_agent: [], by_group: [], cases: [] };
}
